import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/ios-pwa.css";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

// Main app initialization
async function initializeApp() {
  const { BrowserRouter } = await import("react-router-dom");
  const { ClerkProvider } = await import("@clerk/clerk-react");
  const { PortfolioProvider } = await import("./contexts/PortfolioContext");
  const { CurrencyProvider } = await import("./contexts/CurrencyContext");
  const { default: ErrorBoundary } = await import("./ErrorBoundary.jsx");

  const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!PUBLISHABLE_KEY) {
    throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
  }

  // iOS PWA fixes
  if (isIOS) {
    try {
      const { initializeIOSFixes } = await import("./utils/iOSFixes.js");
      initializeIOSFixes();
    } catch (e) {
      // non-critical
    }
  }

  return { BrowserRouter, ClerkProvider, PortfolioProvider, CurrencyProvider, ErrorBoundary, PUBLISHABLE_KEY };
}

const container = document.getElementById("root");
if (!container) throw new Error("Root container not found");

const root = createRoot(container);

async function startApp() {
  try {
    const { BrowserRouter, ClerkProvider, PortfolioProvider, CurrencyProvider, ErrorBoundary, PUBLISHABLE_KEY } = await initializeApp();
    const { default: App } = await import("./App.jsx");

    root.render(
      <StrictMode>
        <ErrorBoundary>
          <ClerkProvider
            publishableKey={PUBLISHABLE_KEY}
            appearance={{ baseTheme: 'dark' }}
          >
            <BrowserRouter future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
              v7_normalizeFormMethod: true,
              v7_skipActionErrorRevalidation: true,
            }}>
              <CurrencyProvider>
                <PortfolioProvider>
                  <App />
                </PortfolioProvider>
              </CurrencyProvider>
            </BrowserRouter>
          </ClerkProvider>
        </ErrorBoundary>
      </StrictMode>
    );
  } catch (error) {
    console.error('Failed to start app:', error);
  }
}

startApp();

// Service worker registration for PWA
if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
  window.addEventListener('load', () => {
    setTimeout(async () => {
      try {
        await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      } catch (e) {
        console.warn('Service Worker registration failed:', e);
      }
    }, 100);
  });
}
